/**
 * `useProjects` — live read of every project the signed-in user is
 * permitted to see within their primary org.
 *
 * MIRRORS the mobile `src/features/projects/useProjects.ts` hook:
 *   1. Subscribe to `projects` collection scoped by `orgId`
 *   2. Apply role-based narrowing client-side:
 *        - App Owner / org owner / studio admin → no extra filter
 *        - Client role (`roles[uid] === 'client'`)      → `clientUids.includes(uid)`
 *        - Anyone else (member, supervisor, manager, …) → `memberIds.includes(uid)`
 *   3. Sort by `createdAt` desc client-side (mobile does the same)
 *
 * The Firestore rule (line 386) gates reads to org members, so the
 * Web SDK can `onSnapshot` this collection directly with no Cloud
 * Function in front.
 *
 * For the web client we don't ALSO join in transactions / approvals
 * counts (those drive mobile's finance summary card at the top of
 * the list) — that's a follow-up. The projects collection alone has
 * everything per-card needs (name, address, status, progress, dates).
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';
import { useFullOrganization } from '@/lib/useFullOrganization';

/** Local Project shape — kept narrow (only fields the list page
 *  reads). The mobile app has a richer type; we cast loosely here
 *  because the project doc has many optional fields and we don't
 *  want to crash on legacy docs missing a field. */
export type ProjectListItem = {
  id: string;
  orgId?: string;
  name: string;
  siteAddress?: string;
  location?: string;
  status?: 'active' | 'on_hold' | 'completed' | 'archived';
  typology?: 'residential' | 'commercial' | 'hospitality' | 'industrial' | 'other';
  phase?: 'design' | 'drawing_2d' | 'render_3d' | 'execution' | 'handover';
  progress?: number; // 0–100
  startDate?: { toMillis(): number } | null;
  endDate?: { toMillis(): number } | null;
  createdAt?: { toMillis(): number } | null;
  memberIds?: string[];
  clientUids?: string[];
  ownerId?: string;
  client?: string;
  value?: number;
  photoUri?: string | null;
};

export type UseProjectsResult = {
  projects: ProjectListItem[];
  loading: boolean;
  error: string | null;
  /** orgId in scope — null when user has no primary org yet. */
  orgId: string | null;
};

export function useProjects(): UseProjectsResult {
  const { user, loading: authLoading } = useAuth();
  const { org, loading: orgLoading } = useFullOrganization();
  const [allProjects, setAllProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = org?.id ?? null;

  useEffect(() => {
    if (authLoading || orgLoading) return;
    if (!user || !orgId) {
      setAllProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(collection(db, 'projects'), where('orgId', '==', orgId)),
      (snap) => {
        setAllProjects(
          snap.docs.map((d) => ({
            ...(d.data() as ProjectListItem),
            id: d.id,
          })),
        );
        setLoading(false);
      },
      (err) => {
        console.warn('[useProjects] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [user, orgId, authLoading, orgLoading]);

  // Role-based narrowing — same priority order as mobile.
  // `roles` is on the org doc as `{ [uid]: 'client' | 'admin' | ... }`.
  // We treat anyone NOT in `roles` (but in `memberIds`) as a regular
  // member, narrowing to projects where their uid is in `memberIds`.
  const projects = useMemo(() => {
    if (!user) return [];
    const orgData = (org?.data ?? {}) as {
      ownerId?: string;
      roles?: Record<string, string>;
    };
    const isOrgOwner = orgData.ownerId === user.uid;
    const role = (orgData.roles ?? {})[user.uid];
    const isClient = role === 'client';

    let filtered = allProjects;
    if (!isOrgOwner) {
      if (isClient) {
        filtered = allProjects.filter((p) =>
          Array.isArray(p.clientUids) && p.clientUids.includes(user.uid),
        );
      } else {
        filtered = allProjects.filter((p) =>
          Array.isArray(p.memberIds) && p.memberIds.includes(user.uid),
        );
      }
    }

    // Sort: createdAt desc (most recent first). Projects with no
    // createdAt (legacy docs) fall to the end.
    return [...filtered].sort((a, b) => {
      const am = a.createdAt?.toMillis?.() ?? 0;
      const bm = b.createdAt?.toMillis?.() ?? 0;
      return bm - am;
    });
  }, [allProjects, user, org]);

  return { projects, loading, error, orgId };
}
